import rawCatalog from "../../../tools/skill-catalog-collector/data/skill-catalog.json" with { type: "json" };
import type { SkillCatalogReader, SkillRecord } from "./skill-catalog.ts";

interface LocalSkillRecord {
  slug: string;
  name: string;
  summary: string;
  description_markdown: string;
  source_url: string;
  author?: string | null;
  tags?: string[];
  version?: string | null;
  command?: string | null;
  command_source_url?: string | null;
}

const records = (rawCatalog as LocalSkillRecord[]).filter((row) => row && typeof row.slug === "string");
const byId: Map<string, LocalSkillRecord> = new Map(records.map((row) => [`skill_${row.slug}`, row] as const));

/**
 * The Skill page is a local data browser. It deliberately has no network
 * dependency and never exposes installation or installation-state mutations.
 */
export class LocalSkillCatalog implements SkillCatalogReader {
  async list(): Promise<SkillRecord[]> {
    return records.map(toRecord);
  }

  async get(skillId: string): Promise<SkillRecord> {
    const row = byId.get(skillId.trim());
    if (!row) throw new Error("Skill was not found in the local catalog.");
    return toRecord(row);
  }
}

function toRecord(row: LocalSkillRecord): SkillRecord {
  const id = `skill_${row.slug}`;
  return {
    id,
    slug: row.slug,
    name: row.name,
    summary: row.summary,
    description: row.description_markdown,
    author: row.author?.trim() || null,
    sourceUrl: row.source_url,
    tags: (row.tags || []).map((tag) => tag.trim()).filter(Boolean),
    version: explicitVersion(row.version),
    command: row.command?.trim() || null,
    commandSourceUrl: row.command_source_url?.trim() || null,
  };
}

function explicitVersion(value: string | null | undefined): string | null {
  // The collector historically wrote 1.0.0 as a placeholder. Do not present
  // that synthetic value as a real upstream release version.
  if (!value || value.trim() === "1.0.0") return null;
  return value.trim();
}
